// Creature state is a pure function of musical time + project state (+ live hits). Nothing here
// reacts to "step N played" messages: sequenced hits are found by looking at where the *audible*
// playhead is relative to this pad's notes, so the creature lands its hit on the audible onset and
// can wind up before it (the pattern is known in advance).
import type { AudiblePosition } from "../lib/clock";
import { TICKS_PER_BAR, ticksToMs, unswingTick } from "../lib/timing";
import { restPose, type CreaturePose } from "./pose";

export const HIT_MS = 280;
export const ANTICIPATE_MS = 60;
export const SHUSH_MS = 320;
export const FLINCH_MS = 350;
export const CHEER_MS = 700;
export const ACCENT_VELOCITY = 110;

/** One of this pad's notes, `tick` already swung (as the engine plays it). */
export interface PadNote {
  tick: number;
  velocity: number;
  length: number;
  /** MIDI note (instrument slots). */
  note?: number;
}

export interface HitInfo {
  /** ms since the most recent audible hit (Infinity if none). */
  agoMs: number;
  velocity: number;
  /** How long the creature should hold after the hit, in ms (0 = no hold). */
  holdMs: number;
  /** ms until the next scheduled hit (Infinity if none). */
  untilMs: number;
  live: boolean;
}

export interface HitInput {
  nowMs: number;
  audible: AudiblePosition;
  bpm: number;
  patternLength: number;
  /** Sorted by tick. */
  notes: readonly PadNote[];
  liveHitAt: number;
  liveHitVel: number;
  liveHeld: boolean;
  gate: boolean;
  /** Sample duration in ms (one-shots hold their mouth open for long samples). */
  sampleMs: number;
}

const LONG_SAMPLE_MS = 400;

/** Find the most recent and next hit for a pad from the audible position and its notes. */
export function hitInfo(h: HitInput): HitInfo {
  let agoMs = Infinity;
  let velocity = 0;
  let holdMs = 0;
  let untilMs = Infinity;
  let live = false;
  const a = h.audible;
  const n = h.notes.length;
  if (a.playing && !a.countIn && n > 0 && h.patternLength > 0) {
    const t = a.tick;
    // Last note at or before t (binary search), wrapping to the previous loop.
    let lo = 0;
    let hi = n;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (h.notes[mid].tick <= t) lo = mid + 1;
      else hi = mid;
    }
    const prevIdx = lo - 1;
    let prev: PadNote | null = null;
    let sinceTicks = Infinity;
    if (prevIdx >= 0) {
      prev = h.notes[prevIdx];
      sinceTicks = t - prev.tick;
    } else if (a.transportTick >= t + (h.patternLength - h.notes[n - 1].tick)) {
      prev = h.notes[n - 1];
      sinceTicks = t + (h.patternLength - prev.tick);
    }
    if (prev) {
      agoMs = ticksToMs(sinceTicks, h.bpm);
      velocity = prev.velocity;
      holdMs = h.gate ? ticksToMs(prev.length, h.bpm) : h.sampleMs > LONG_SAMPLE_MS ? Math.min(h.sampleMs, 4000) : 0;
    }
    const next = lo < n ? h.notes[lo] : h.notes[0];
    const untilTicks = lo < n ? next.tick - t : h.patternLength - t + next.tick;
    untilMs = ticksToMs(untilTicks, h.bpm);
  }
  if (h.liveHitAt > 0) {
    const liveAgo = h.nowMs - h.liveHitAt;
    if (liveAgo >= 0 && liveAgo < agoMs) {
      agoMs = liveAgo;
      velocity = h.liveHitVel;
      live = true;
      holdMs = h.gate
        ? h.liveHeld
          ? liveAgo + 1000
          : 0
        : h.sampleMs > LONG_SAMPLE_MS
          ? Math.min(h.sampleMs, 4000)
          : 0;
    }
  }
  return { agoMs, velocity, holdMs, untilMs, live };
}

export interface PoseInput {
  nowMs: number;
  audible: AudiblePosition;
  bpm: number;
  swing: number;
  hit: HitInfo;
  muted: boolean;
  soloed: boolean;
  anySolo: boolean;
  /** Semitones. */
  tune: number;
  pan: number;
  chokeGroup: number;
  /** ms since a choke-group mate last hit (Infinity if none). */
  mateHitAgoMs: number;
  /** 0–1 position of this creature in the cheer wave. */
  ripple: number;
  seed: number;
  reducedMotion: boolean;
}

const TAU = Math.PI * 2;
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const easeOut = (t: number) => 1 - (1 - t) * (1 - t);

/** Squash curve over a hit: impact squash → stretch while airborne → landing squash. */
export function hitSquash(phase: number): number {
  if (phase < 0.12) return 0.7 * (1 - phase / 0.12);
  if (phase < 0.72) return -0.35 * Math.sin((Math.PI * (phase - 0.12)) / 0.6);
  return 0.4 * Math.sin((Math.PI * (phase - 0.72)) / 0.28);
}

export function computePose(p: PoseInput, out: CreaturePose = restPose()): CreaturePose {
  const motion = p.reducedMotion ? 0.25 : 1;
  const beats = p.audible.playing ? p.audible.transportTick / 960 : (p.nowMs / 60000) * p.bpm;

  out.state = "idle";
  out.phase = 0;
  out.squash = 0;
  out.jump = 0;
  out.spin = 0;
  out.lean = Math.max(-1, Math.min(1, p.pan));
  out.scale = Math.min(1.25, Math.max(0.7, Math.pow(2, -p.tune / 48)));
  out.hueShift = p.tune * 3;
  out.mouthOpen = 0;
  out.glow = 0.12;
  out.ripple = 0;
  out.dim = p.anySolo && !p.soloed ? 0.6 : 0;
  out.spotlight = p.soloed;
  out.flinch = 0;
  out.t = 0;

  // Idle: breathing on the beat (one breath per 2 beats), seeded blinks, swing-following bob.
  // Calm idle: one slow breath per 4 beats and a gentle bob every 2 beats (shaped by the swing).
  // Seeds of neighbouring creatures (e.g. the keys of one instrument) differ by 1, so scramble
  // them first or they would blink and breathe in lockstep.
  const hs = mixSeed(p.seed);
  out.breath = Math.sin(TAU * (beats / 4 + (hs % 997) / 997)) * 0.7 * motion;
  const cycle = 2600 + ((hs >>> 8) % 2400);
  const bt = (p.nowMs + ((hs >>> 4) % cycle)) % cycle;
  out.blink = bt < 130 ? Math.sin((Math.PI * bt) / 130) : 0;
  const twoBeats = (((beats * 960) % 1920) + 1920) % 1920;
  const swungPhase = unswingTick(twoBeats / 4, p.swing) / 480; // swing warps the bob's half-way point
  out.bob = Math.sin(TAU * swungPhase) * 0.25 * motion;

  if (p.muted) {
    out.state = "sleep";
    out.blink = 1;
    out.breath = Math.sin(TAU * (p.nowMs / 4000)) * motion;
    out.bob = 0;
    out.t = p.nowMs / 1000;
    return out;
  }

  const h = p.hit;
  if (p.mateHitAgoMs < FLINCH_MS && p.chokeGroup > 0) {
    out.flinch = (1 - p.mateHitAgoMs / FLINCH_MS) * motion;
  }

  if (h.agoMs < HIT_MS) {
    const phase = h.agoMs / HIT_MS;
    const v = clamp01(h.velocity / 127);
    const accent = h.velocity >= ACCENT_VELOCITY;
    out.state = accent ? "accent" : "hit";
    out.phase = phase;
    out.t = h.agoMs / 1000;
    out.jump = (accent ? 0.95 : 0.6) * v * Math.sin(Math.PI * phase) * motion;
    out.squash = hitSquash(phase) * motion;
    out.spin = accent && !p.reducedMotion ? TAU * easeOut(phase) : 0;
    out.mouthOpen = 1 - phase * 0.5;
    out.glow = 0.4 + 0.6 * (1 - phase);
    out.ripple = 0.6 * (1 - phase);
    out.blink = 0;
    return out;
  }

  if (p.chokeGroup > 0 && h.agoMs < HIT_MS + SHUSH_MS && h.agoMs !== Infinity) {
    out.state = "shush";
    out.phase = (h.agoMs - HIT_MS) / SHUSH_MS;
    out.t = h.agoMs / 1000;
    out.mouthOpen = 0;
    return out;
  }

  if (h.holdMs > 0 && h.agoMs < h.holdMs) {
    out.state = "hold";
    out.phase = h.agoMs / h.holdMs;
    out.t = h.agoMs / 1000;
    out.mouthOpen = 0.8;
    out.glow = 0.85;
    out.ripple = 0.75;
    out.squash = 0.06 * Math.sin(TAU * (h.agoMs / 180)) * motion;
    return out;
  }

  if (h.untilMs <= ANTICIPATE_MS) {
    const phase = 1 - h.untilMs / ANTICIPATE_MS;
    out.state = "anticipate";
    out.phase = phase;
    out.squash = 0.35 * phase * motion;
    out.mouthOpen = 0.15 * phase;
    return out;
  }

  // Cheer: at the downbeat of every 4th bar a wave ripples across the creatures.
  if (p.audible.playing && p.audible.transportTick >= 4 * TICKS_PER_BAR) {
    const cyc = p.audible.transportTick % (4 * TICKS_PER_BAR);
    const ago = ticksToMs(cyc, p.bpm) - p.ripple * 400;
    if (ago >= 0 && ago < CHEER_MS) {
      const phase = ago / CHEER_MS;
      out.state = "cheer";
      out.phase = phase;
      out.t = ago / 1000;
      out.jump = 0.3 * Math.sin(Math.PI * phase) * motion;
      out.squash = -0.15 * Math.sin(Math.PI * phase) * motion;
      out.mouthOpen = 0.7;
      return out;
    }
  }

  return out;
}

/** 32-bit integer hash (murmur3 finaliser): nearby seeds map to unrelated values. */
export function mixSeed(seed: number): number {
  let h = seed >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}
